import React, { useState, useEffect } from 'react';
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon, PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { LearningContentType } from '../../types';

interface LearningContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (contentData: FormData) => void;
  sectionId: string;
  isLoading: boolean;
}

const LearningContentModal: React.FC<LearningContentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  sectionId,
  isLoading
}) => {
  const [formData, setFormData] = useState({
    title: '',
    contentType: LearningContentType.VIDEO,
    url: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        contentType: LearningContentType.VIDEO,
        url: '',
      });
      setSelectedFile(null);
      setDragActive(false);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('contentType', formData.contentType);
    submitData.append('sectionId', sectionId);
    
    if (formData.contentType === LearningContentType.VIDEO) {
      submitData.append('url', formData.url);
    } else if (selectedFile) {
      submitData.append('file', selectedFile);
    }
    
    onSubmit(submitData);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    
    // Auto-detect content type based on file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension) {
      if (['pdf'].includes(extension)) {
        setFormData(prev => ({ ...prev, contentType: LearningContentType.PDF }));
      } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
        setFormData(prev => ({ ...prev, contentType: LearningContentType.IMAGE }));
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const getContentTypeIcon = (type: LearningContentType) => {
    switch (type) {
      case LearningContentType.VIDEO:
        return <VideoCameraIcon className="h-5 w-5" />;
      case LearningContentType.PDF:
        return <DocumentIcon className="h-5 w-5" />;
      case LearningContentType.IMAGE:
        return <PhotoIcon className="h-5 w-5" />;
      default:
        return <DocumentIcon className="h-5 w-5" />;
    }
  };

  const getAcceptedFileTypes = () => {
    switch (formData.contentType) {
      case LearningContentType.PDF:
        return '.pdf';
      case LearningContentType.IMAGE:
        return '.jpg,.jpeg,.png,.gif,.webp';
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Learning Content</h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[LearningContentType.VIDEO, LearningContentType.PDF, LearningContentType.IMAGE].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, contentType: type });
                          setSelectedFile(null);
                        }}
                        className={`flex items-center space-x-2 p-3 border rounded-md transition-colors ${
                          formData.contentType === type
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {getContentTypeIcon(type)}
                        <span className="text-sm font-medium">{type}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Input */}
                {formData.contentType === LearningContentType.VIDEO ? (
                  <div>
                    <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                      YouTube URL
                    </label>
                    <input
                      type="url"
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload File
                    </label>
                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                        dragActive
                          ? 'border-primary-500 bg-primary-50'
                          : selectedFile
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <div className="text-center">
                        {selectedFile ? (
                          <div className="flex items-center justify-center space-x-2">
                            {getContentTypeIcon(formData.contentType)}
                            <span className="text-sm font-medium text-green-700">
                              {selectedFile.name}
                            </span>
                          </div>
                        ) : (
                          <>
                            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-2">
                              <label htmlFor="file-upload" className="cursor-pointer">
                                <span className="text-primary-600 hover:text-primary-500 font-medium">
                                  Upload a file
                                </span>
                                <span className="text-gray-500"> or drag and drop</span>
                              </label>
                              <input
                                id="file-upload"
                                type="file"
                                className="sr-only"
                                accept={getAcceptedFileTypes()}
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleFileSelect(e.target.files[0]);
                                  }
                                }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {formData.contentType === LearningContentType.PDF && 'PDF files up to 50MB'}
                              {formData.contentType === LearningContentType.IMAGE && 'Images up to 50MB'}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isLoading || !formData.title || (formData.contentType === LearningContentType.VIDEO ? !formData.url : !selectedFile)}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Adding...' : 'Add Content'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LearningContentModal;
